import { useEffect, useRef, useState } from 'react';
import { createResizeObserver } from '../../../../utils/createResizeObserver';
import { ASK_AICA_ORB_GAP_VAR, ASK_AICA_ORB_HEIGHT_VAR, ASK_AICA_ORB_OFFSET_VAR, BANK_STATEMENTS_ORB_CLEARANCE_VAR } from './constants';
import { OrbClearanceState } from './types';

export function getOrbClearancePx(viewportHeight: number, boxBottom: number, orbTopFromViewportBottom: number, gap: number) {
    return Math.max(0, orbTopFromViewportBottom + gap - (viewportHeight - boxBottom));
}

export function isScrollOverflowing(frameHeight: number, boxClientHeight: number) {
    return frameHeight >= boxClientHeight;
}

function readRootRemToken(name: string) {
    const rootStyle = getComputedStyle(document.documentElement);
    return (parseFloat(rootStyle.getPropertyValue(name)) || 0) * (parseFloat(rootStyle.fontSize) || 16);
}

function observeBoxAndFrame(state: OrbClearanceState) {
    const { el, resizeObserver, mutationObserver } = state;
    const frame = el.firstElementChild;
    if (frame === state.observedFrame) return;
    state.observedFrame = frame;
    resizeObserver?.disconnect();
    mutationObserver.disconnect();
    resizeObserver?.observe(el);
    mutationObserver.observe(el, { childList: true });
    if (!frame) return;
    resizeObserver?.observe(frame);
    mutationObserver.observe(frame, { childList: true });
}

function readClearance(state: OrbClearanceState) {
    const { el } = state;
    state.frameId = 0;
    state.onOverflowChange(isScrollOverflowing((el.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0, el.clientHeight));
    const clearancePx = getOrbClearancePx(window.innerHeight, el.getBoundingClientRect().bottom, state.orbTopFromViewportBottom, state.gap);
    if (clearancePx === state.lastClearancePx) return;
    state.lastClearancePx = clearancePx;
    el.style.setProperty(BANK_STATEMENTS_ORB_CLEARANCE_VAR, `${clearancePx}px`);
}

function scheduleRead(state: OrbClearanceState) {
    if (!state.frameId) state.frameId = requestAnimationFrame(() => readClearance(state));
}

function onScrollOutsideBox(state: OrbClearanceState, event: Event) {
    if (!(event.target instanceof Node && state.el.contains(event.target))) scheduleRead(state);
}

function createOrbClearanceState(el: HTMLElement, onOverflowChange: (isOverflowing: boolean) => void): OrbClearanceState {
    const state: OrbClearanceState = {
        el,
        onOverflowChange,
        orbTopFromViewportBottom: readRootRemToken(ASK_AICA_ORB_OFFSET_VAR) + readRootRemToken(ASK_AICA_ORB_HEIGHT_VAR),
        gap: readRootRemToken(ASK_AICA_ORB_GAP_VAR),
        frameId: 0,
        lastClearancePx: -1,
        observedFrame: undefined,
        resizeObserver: createResizeObserver(() => scheduleRead(state)),
        mutationObserver: new MutationObserver(() => {
            observeBoxAndFrame(state);
            scheduleRead(state);
        }),
    };
    return state;
}

function observeContentOverflow(el: HTMLElement, onOverflowChange: (isOverflowing: boolean) => void) {
    const state = createOrbClearanceState(el, onOverflowChange);
    const onScroll = onScrollOutsideBox.bind(null, state);
    const onResize = scheduleRead.bind(null, state);

    readClearance(state);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    observeBoxAndFrame(state);

    return () => {
        cancelAnimationFrame(state.frameId);
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
        state.resizeObserver?.disconnect();
        state.mutationObserver.disconnect();
    };
}

export function useOrbClearance(selectedBankingType: string, selectedAccountId: string | null) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return undefined;
        return observeContentOverflow(el, setIsContentOverflowing);
    }, [selectedBankingType, selectedAccountId]);

    return { contentRef, isContentOverflowing };
}
